/// <reference types="@shopify/polaris-types" />
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NavigationBlocker } from "../../components/common/NavigationBlocker";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { useOtpBalance } from "../../hooks/useOtpBalance";
import {
  useOtpConfigStore,
  selectOtpConfigIsDirty,
} from "../../store/useOtpConfigStore";
import { SecurityTabBar } from "./SecurityTabBar";
import { OtpBalanceSection } from "./OtpBalanceSection";
import { OtpChannelExplainerPanel } from "./OtpChannelExplainerPanel";
import { OtpConfigForm } from "./OtpConfigForm";
import { OtpUiTextsEditor } from "./OtpUiTextsEditor";
import { OtpWidgetLivePreview } from "./OtpWidgetLivePreview";
import { OtpTestSection } from "./OtpTestSection";

export default function OtpConfigPage() {
  const [searchParams] = useSearchParams();
  const hasChargeId = searchParams.has("charge_id");
  const [billingWasPending] = useState(
    () => localStorage.getItem("codway_otp_billing_pending") === "1"
  );
  useEffect(() => {
    if (billingWasPending)
      localStorage.removeItem("codway_otp_billing_pending");
  }, [billingWasPending]);

  const loadFromBackend = useOtpConfigStore((s) => s.loadFromBackend);
  const save = useOtpConfigStore((s) => s.save);
  const discard = useOtpConfigStore((s) => s.discardChanges);
  const isDirty = useOtpConfigStore(selectOtpConfigIsDirty);
  const isSaving = useOtpConfigStore((s) => s.isSaving);
  const isLoading = useOtpConfigStore((s) => s.isLoading);
  const error = useOtpConfigStore((s) => s.error);
  const channel = useOtpConfigStore((s) => s.channel);
  const uiTexts = useOtpConfigStore((s) => s.uiTexts);

  const balance = useOtpBalance();

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  const heading =
    channel === "sms" ? "Mensajes OTP de SMS" : "Mensajes OTP de WhatsApp";

  if (isLoading || balance.loading) {
    return (
      <s-page heading={heading}>
        <SecurityTabBar />
        <s-box padding="large-400">
          <s-stack direction="block" gap="base" alignItems="center">
            <s-spinner />
            <s-text color="subdued">Cargando configuración...</s-text>
          </s-stack>
        </s-box>
      </s-page>
    );
  }

  if (error) {
    return (
      <s-page heading={heading}>
        <SecurityTabBar />
        <s-banner tone="critical">
          <s-text>Error al cargar: {error}</s-text>
        </s-banner>
      </s-page>
    );
  }

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={heading}>
        <s-stack direction="block" gap="large-400">
          <SecurityTabBar />

          {hasChargeId && (
            <s-banner tone="success" heading="¡Compra procesada!" dismissible>
              Tu pago fue aprobado. El saldo se acreditará en unos segundos.
            </s-banner>
          )}
          {billingWasPending && !hasChargeId && (
            <s-banner tone="warning" heading="Compra cancelada" dismissible>
              No se realizó ningún cargo. Puedes intentarlo de nuevo cuando
              quieras.
            </s-banner>
          )}

          {balance.error && (
            <s-banner tone="critical">
              <s-text>{balance.error}</s-text>
            </s-banner>
          )}

          <s-grid gridTemplateColumns="3fr 2fr" gap="base">
            {balance.data && (
              <OtpBalanceSection
                balance={balance.data}
                buying={balance.buying}
                updatingContacts={balance.updatingContacts}
                onBuy={balance.buyPack}
                onUpdateContacts={balance.updateNotificationContacts}
              />
            )}
            <OtpChannelExplainerPanel
              key={balance.data?.pricing?.country_code ?? "loading"}
              channel={channel}
              pricing={balance.data?.pricing ?? null}
            />
          </s-grid>

          <OtpConfigForm />

          <s-section heading="Textos personalizables del widget">
            <s-grid gridTemplateColumns="3fr 2fr" gap="large-400">
              <OtpUiTextsEditor />
              <OtpWidgetLivePreview uiTexts={uiTexts} />
            </s-grid>
          </s-section>

          <OtpTestSection isDirty={isDirty} />
        </s-stack>
      </s-page>
    </div>
  );
}
